import React, {useCallback, useEffect, useMemo, useState} from "react";
import axios from "axios";
import {
    Avatar,
    Button,
    Checkbox,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    Input,
    Option,
    Select,
    Typography
} from "@material-tailwind/react";
import {
    ArrowPathIcon,
    BoltIcon,
    CheckIcon,
    CheckCircleIcon,
    ClipboardDocumentIcon,
    DocumentTextIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    InformationCircleIcon,
    MinusCircleIcon,
    PencilSquareIcon,
    PlusCircleIcon,
    TrashIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import {FolderIcon as FolderSolidIcon} from "@heroicons/react/24/solid";
import {toLocalDateTimeInputValue} from "../../utils/dateTime";
import AutomationDialog from "./AutomationDialog";
import BatchAutomationDialog from "./BatchAutomationDialog";

const FOLDERS = [
    { value: 'ORDERS_PROCESS', label: 'Orders Process', shortLabel: 'Process' },
    { value: 'ORDERS_DONE', label: 'Orders Done', shortLabel: 'Done' },
];

const SIDEBAR_FOLDERS = [
    { value: 'ORDERS_DONE', label: 'Orders Done', shortLabel: 'Done', folders: ['ORDERS_DONE'] },
    { value: 'PROCESS', label: 'Process', shortLabel: 'Process', folders: ['ORDERS_PROCESS'] },
];

const FOLDER_STYLES = {
    ORDERS_PROCESS: {
        iconLight: '#D4A45F',
        iconDark: '#E2B66E',
        backgroundLight: '#F6EBD8',
        backgroundHoverLight: '#F1DFC0',
        backgroundDark: '#352819',
        backgroundHoverDark: '#44331F',
        borderLight: '#EBD4AF',
        borderDark: '#6A4C24',
    },
    ORDERS_DONE: {
        iconLight: '#C77E8A',
        iconDark: '#D9909D',
        backgroundLight: '#F4E4E7',
        backgroundHoverLight: '#ECD4D9',
        backgroundDark: '#351D23',
        backgroundHoverDark: '#43262D',
        borderLight: '#E7C8CE',
        borderDark: '#70414B',
    },
};

const STATUS_LABELS = {
    PLACED: "Placed",
    DISPATCHED: "Dispatched",
    RECEIVED_UK: "Received (UK)",
    IN_TRANSIT: "In Transit",
    IN_STOCK: "In Stock",
    SOLD: "Sold",
    CANCELLED: "Cancelled",
    GIVEAWAY: "Giveaway",
    IN_STOCK_PERSONAL: "Personal",
    DISPATCHED_PERSONAL: "Personal Dispatched",
    DAMAGED: "Damaged",
};

const EMPTY_FORM = {
    email: '',
    folder: 'ORDERS_PROCESS',
    automationProfileNumber: '',
    note: '',
    rawContent: '',
};

const EMPTY_EVENT_FORM = {
    type: 'CASE_CREATED',
    eventDateText: '',
    eventDateInputValue: '',
    ticketNumber: '',
    ticketEntries: [],
    replacementTitleEntries: [],
    replacementTitle: '',
    eanIsbn: '',
    agentName: '',
    note: '',
};

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
const OPENED_ORDERS_STORAGE_KEY = 'arcado.orders.activeOrder.v2';
const ORDER_ITEM_ACTIONS_STORAGE_KEY = 'arcado.orders.itemActions';
const ORDER_EXPANDED_ITEM_EVENTS_STORAGE_KEY = 'arcado.orders.expandedItemEvents';

const formatCurrency = (value) => {
    if (!Number.isFinite(value)) return '/';
    return value.toLocaleString('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const formatDisplayDate = (value) => {
    if (!value) return '/';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '/';

    return new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).format(date);
};

const formatOrderPurchaseDate = (value) => {
    if (!value) return '/';

    const match = value.trim().match(/^(\d{1,2})\.(\d{1,2})(?:\.(\d{4}))?\.?$/);
    if (!match) return '/';

    const date = new Date(
        Number(match[3] || new Date().getFullYear()),
        Number(match[2]) - 1,
        Number(match[1])
    );
    return Number.isNaN(date.getTime()) ? '/' : formatDisplayDate(date);
};

const getStatusBadgeClass = (status) => `text-center inline-flex px-2 py-0.5 text-xs font-semibold rounded-md
    ${status === "PLACED" ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" : ""}
    ${status === "DISPATCHED" ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" : ""}
    ${status === "RECEIVED_UK" ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" : ""}
    ${status === "IN_TRANSIT" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" : ""}
    ${status === "IN_STOCK" ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : ""}
    ${status === "SOLD" ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" : ""}
    ${status === "CANCELLED" ? "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300" : ""}
    ${status === "GIVEAWAY" ? "bg-black text-white dark:bg-gray-900 dark:text-gray-100" : ""}
    ${status === "IN_STOCK_PERSONAL" ? "bg-green-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300" : ""}
    ${status === "DISPATCHED_PERSONAL" ? "bg-yellow-100 text-lime-800 dark:bg-amber-900/30 dark:text-amber-300" : ""}
    ${status === "DAMAGED" ? "bg-red-300 text-black dark:bg-red-800/40 dark:text-red-200" : ""}
    border border-gray-300 dark:border-gray-600`;

const toDateTimeInputValue = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatEventDateText = (dateTimeInputValue) => {
    if (!dateTimeInputValue) return '';

    const [datePart, timePart] = dateTimeInputValue.split('T');
    const [year, month, day] = (datePart || '').split('-');
    if (!year || !month || !day) return '';

    return `${day}.${month}.${year} ${timePart || '00:00'}`;
};

const normalizeEventDateTextForDisplay = (eventDateText) => {
    if (!eventDateText) return '';

    const trimmedDateText = eventDateText.trim();
    if (/^\d{1,2}\.\d{1,2}$/.test(trimmedDateText)) {
        const [day, month] = trimmedDateText.split('.');
        return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${new Date().getFullYear()} 00:00`;
    }

    return trimmedDateText;
};

const formatActionDate = (date = new Date()) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
};

const truncateText = (value, maxLength = 20) => {
    const text = (value || '').trim();
    if (text.length <= maxLength) return text;

    return `${text.slice(0, maxLength)}...`;
};

const eventDateTextToDateInputValue = (eventDateText) => {
    if (!eventDateText) return '';

    const [datePart, timePart = '00:00'] = eventDateText.trim().split(/\s+/);
    const [day, month, year] = datePart.split('.');
    if (!day || !month) return '';

    const normalizedDay = day.padStart(2, '0');
    const normalizedMonth = month.padStart(2, '0');
    const normalizedYear = (year || String(new Date().getFullYear())).replace(/\.$/, '');
    const normalizedTime = /^\d{1,2}:\d{2}$/.test(timePart)
        ? `${timePart.split(':')[0].padStart(2, '0')}:${timePart.split(':')[1]}`
        : '00:00';

    return `${normalizedYear}-${normalizedMonth}-${normalizedDay}T${normalizedTime}`;
};

const createTicketEntry = (eventDateInputValue = toDateTimeInputValue(), ticketNumber = '') => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    eventDateInputValue,
    eventDateText: formatEventDateText(eventDateInputValue),
    ticketNumber,
});

const createReplacementTitleEntry = (
    eventDateInputValue = toDateTimeInputValue(),
    replacementTitle = '',
    eanIsbn = '',
    sameItem = false
) => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    eventDateInputValue,
    eventDateText: formatEventDateText(eventDateInputValue),
    replacementTitle,
    eanIsbn,
    sameItem,
});

const parseTicketEntries = (event) => {
    if (!event) return [];

    let parsedEntries = [];
    if (event.ticketEntriesText) {
        try {
            const jsonEntries = JSON.parse(event.ticketEntriesText);
            if (Array.isArray(jsonEntries)) {
                parsedEntries = jsonEntries
                    .map((entry) => ({
                        eventDateText: typeof entry?.eventDateText === 'string' ? entry.eventDateText : '',
                        ticketNumber: typeof entry?.ticketNumber === 'string' ? entry.ticketNumber : '',
                    }))
                    .filter((entry) => entry.eventDateText || entry.ticketNumber);
            }
        } catch (error) {
            console.error('Error parsing ticket entries:', error);
        }
    }

    if (parsedEntries.length === 0 && (event.eventDateText || event.ticketNumber)) {
        parsedEntries = [{
            eventDateText: event.eventDateText || '',
            ticketNumber: event.ticketNumber || '',
        }];
    }

    return parsedEntries.map((entry) => ({
        id: createTicketEntry().id,
        eventDateText: entry.eventDateText,
        eventDateInputValue: eventDateTextToDateInputValue(entry.eventDateText),
        ticketNumber: entry.ticketNumber,
    }));
};

const serializeTicketEntries = (ticketEntries) => JSON.stringify(ticketEntries.map((entry) => ({
    eventDateText: entry.eventDateText.trim(),
    ticketNumber: entry.ticketNumber.trim(),
})));

const parseReplacementTitleEntries = (event) => {
    if (!event) return [];

    let parsedEntries = [];
    if (event.ticketEntriesText) {
        try {
            const jsonEntries = JSON.parse(event.ticketEntriesText);
            if (Array.isArray(jsonEntries)) {
                parsedEntries = jsonEntries
                    .map((entry) => ({
                        eventDateText: typeof entry?.eventDateText === 'string' ? entry.eventDateText : '',
                        replacementTitle: typeof entry?.replacementTitle === 'string' ? entry.replacementTitle : '',
                        eanIsbn: typeof entry?.eanIsbn === 'string' ? entry.eanIsbn : '',
                        sameItem: Boolean(entry?.sameItem),
                    }))
                    .filter((entry) => entry.eventDateText || entry.replacementTitle || entry.eanIsbn);
            }
        } catch (error) {
            console.error('Error parsing replacement title entries:', error);
        }
    }

    if (parsedEntries.length === 0 && (event.eventDateText || event.replacementTitle || event.eanIsbn)) {
        parsedEntries = [{
            eventDateText: event.eventDateText || '',
            replacementTitle: event.replacementTitle || '',
            eanIsbn: event.eanIsbn || '',
            sameItem: false,
        }];
    }

    return parsedEntries.map((entry) => ({
        id: createReplacementTitleEntry().id,
        eventDateText: entry.eventDateText,
        eventDateInputValue: eventDateTextToDateInputValue(entry.eventDateText),
        replacementTitle: entry.replacementTitle,
        eanIsbn: entry.eanIsbn,
        sameItem: entry.sameItem,
    }));
};

const serializeReplacementTitleEntries = (entries) => JSON.stringify(entries.map((entry) => ({
    eventDateText: entry.eventDateText.trim(),
    replacementTitle: entry.replacementTitle.trim(),
    eanIsbn: entry.eanIsbn.trim(),
    sameItem: Boolean(entry.sameItem),
})));

const loadPersistedItemActions = () => {
    try {
        const persistedItemActions = localStorage.getItem(ORDER_ITEM_ACTIONS_STORAGE_KEY);
        if (!persistedItemActions) return {};

        const parsedItemActions = JSON.parse(persistedItemActions);
        return parsedItemActions && typeof parsedItemActions === 'object' && !Array.isArray(parsedItemActions)
            ? parsedItemActions
            : {};
    } catch (error) {
        console.error('Error loading order item actions:', error);
        return {};
    }
};

const loadPersistedExpandedItemEvents = () => {
    try {
        const persistedExpandedItemEvents = localStorage.getItem(ORDER_EXPANDED_ITEM_EVENTS_STORAGE_KEY);
        if (!persistedExpandedItemEvents) return {};

        const parsedExpandedItemEvents = JSON.parse(persistedExpandedItemEvents);
        return parsedExpandedItemEvents && typeof parsedExpandedItemEvents === 'object' && !Array.isArray(parsedExpandedItemEvents)
            ? parsedExpandedItemEvents
            : {};
    } catch (error) {
        console.error('Error loading expanded order item events:', error);
        return {};
    }
};

const loadPersistedOpenedOrders = () => {
    try {
        const persistedOpenedOrders = localStorage.getItem(OPENED_ORDERS_STORAGE_KEY);
        if (!persistedOpenedOrders) return [];

        const parsedOpenedOrders = JSON.parse(persistedOpenedOrders);
        return Array.isArray(parsedOpenedOrders) ? parsedOpenedOrders.slice(0, 1) : [];
    } catch (error) {
        console.error('Error loading opened orders:', error);
        return [];
    }
};

const STANDARD_ORDER_EVENT_TYPES = [
    { type: 'CASE_CREATED', label: 'Case Created' },
    { type: 'DAMAGE_PHOTO_REQUESTED', label: 'Damage Photo Requested' },
    { type: 'DAMAGE_PHOTO_SUBMITTED', label: 'Damage Photo Submitted' },
    { type: 'REPLACEMENT_TITLE_REQUESTED', label: 'Replacement Title Requested' },
    { type: 'REPLACEMENT_TITLE_SUBMITTED', label: 'Replacement Title Submitted' },
    { type: 'REPLACEMENT_TITLE_APPROVED', label: 'Replacement Title Approved' },
    { type: 'REPLACEMENT_DAMAGE_CASE_CREATED', label: 'Replacement Damage Case Created' },
    { type: 'REPLACEMENT_DAMAGE_PHOTO_REQUESTED', label: 'Replacement Damage Photo Requested' },
    { type: 'REPLACEMENT_DAMAGE_PHOTO_SUBMITTED', label: 'Replacement Damage Photo Submitted' },
];

const BONUS_ORDER_EVENT_TYPES = [
    { type: 'REPLACEMENT_TITLE_REQUESTED_BONUS', label: 'Replacement Title Requested Bonus' },
    { type: 'REPLACEMENT_TITLE_SUBMITTED_BONUS', label: 'Replacement Title Submitted Bonus' },
    { type: 'REPLACEMENT_TITLE_APPROVED_BONUS', label: 'Replacement Title Approved Bonus' },
    { type: 'REPLACEMENT_DAMAGE_CASE_CREATED_BONUS', label: 'Replacement Damage Case Created Bonus' },
    { type: 'REPLACEMENT_DAMAGE_PHOTO_REQUESTED_BONUS', label: 'Replacement Damage Photo Requested Bonus' },
    { type: 'REPLACEMENT_DAMAGE_PHOTO_SUBMITTED_BONUS', label: 'Replacement Damage Photo Submitted Bonus' },
];

const REFUND_ORDER_EVENT_TYPE = { type: 'REFUND_ISSUED', label: 'Refund Issued' };
const ORDER_EVENT_TYPES = [
    ...STANDARD_ORDER_EVENT_TYPES,
    ...BONUS_ORDER_EVENT_TYPES,
    REFUND_ORDER_EVENT_TYPE,
];
const getOrderEventTypes = (item) => [
    ...STANDARD_ORDER_EVENT_TYPES,
    ...(item?.bonusEventsEnabled ? BONUS_ORDER_EVENT_TYPES : []),
    { type: 'REFUND_ISSUED', label: 'Refund Issued' },
];

const BLUE_EVENT_TYPES = [
    'CASE_CREATED',
    'DAMAGE_PHOTO_SUBMITTED',
    'REPLACEMENT_TITLE_SUBMITTED',
    'REPLACEMENT_DAMAGE_CASE_CREATED',
    'REPLACEMENT_DAMAGE_PHOTO_SUBMITTED',
    'REPLACEMENT_TITLE_SUBMITTED_BONUS',
    'REPLACEMENT_DAMAGE_CASE_CREATED_BONUS',
    'REPLACEMENT_DAMAGE_PHOTO_SUBMITTED_BONUS',
];

const YELLOW_EVENT_TYPES = [
    'DAMAGE_PHOTO_REQUESTED',
    'REPLACEMENT_TITLE_REQUESTED',
    'REPLACEMENT_TITLE_APPROVED',
    'REPLACEMENT_DAMAGE_PHOTO_REQUESTED',
    'REPLACEMENT_TITLE_REQUESTED_BONUS',
    'REPLACEMENT_TITLE_APPROVED_BONUS',
    'REPLACEMENT_DAMAGE_PHOTO_REQUESTED_BONUS',
    'REFUND_ISSUED',
];

const TICKET_EVENT_TYPES = [
    'CASE_CREATED',
    'REPLACEMENT_DAMAGE_CASE_CREATED',
    'REPLACEMENT_DAMAGE_CASE_CREATED_BONUS',
];
const REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES = [
    'REPLACEMENT_TITLE_SUBMITTED',
    'REPLACEMENT_TITLE_SUBMITTED_BONUS',
];
const APPROVED_REPLACEMENT_EVENT_TYPES = [
    'REPLACEMENT_TITLE_APPROVED',
    'REPLACEMENT_TITLE_APPROVED_BONUS',
];
const STANDARD_APPROVED_REPLACEMENT_EVENT_TYPE = 'REPLACEMENT_TITLE_APPROVED';
const BONUS_APPROVED_REPLACEMENT_EVENT_TYPE = 'REPLACEMENT_TITLE_APPROVED_BONUS';
const REFUND_ISSUED_EVENT_TYPE = 'REFUND_ISSUED';
const BONUS_TOGGLE_EVENT_TYPE = 'REPLACEMENT_DAMAGE_PHOTO_SUBMITTED';
const SIX_DAYS_IN_MS = 6 * 24 * 60 * 60 * 1000;
const TWENTY_HOURS_IN_MS = 20 * 60 * 60 * 1000;
const CASE_WAIT_EVENT_TYPES = [
    'CASE_CREATED',
    'REPLACEMENT_DAMAGE_CASE_CREATED',
    'REPLACEMENT_DAMAGE_CASE_CREATED_BONUS',
];
const INTERMEDIATE_WAIT_EVENT_TYPES = [
    'DAMAGE_PHOTO_REQUESTED',
    'DAMAGE_PHOTO_SUBMITTED',
    'REPLACEMENT_TITLE_REQUESTED',
    'REPLACEMENT_TITLE_SUBMITTED',
    'REPLACEMENT_DAMAGE_PHOTO_REQUESTED',
    'REPLACEMENT_DAMAGE_PHOTO_SUBMITTED',
    'REPLACEMENT_TITLE_REQUESTED_BONUS',
    'REPLACEMENT_TITLE_SUBMITTED_BONUS',
    'REPLACEMENT_DAMAGE_PHOTO_REQUESTED_BONUS',
    'REPLACEMENT_DAMAGE_PHOTO_SUBMITTED_BONUS',
];

const EVENT_LABELS = ORDER_EVENT_TYPES.reduce((labels, eventType) => ({
    ...labels,
    [eventType.type]: eventType.label,
}), {});

const getEventTypeLabel = (type) => EVENT_LABELS[type] || type || 'Event';

export default function OrdersWorkspace() {
    const token = localStorage.getItem("token");
    const requestHeaders = useMemo(() => ({ 'Authorization': `Bearer ${token}` }), [token]);

    const [selectedFolder, setSelectedFolder] = useState('PROCESS');
    const [orders, setOrders] = useState([]);
    const [folderCounts, setFolderCounts] = useState({});
    const [openedOrders, setOpenedOrders] = useState(loadPersistedOpenedOrders);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [dialogMode, setDialogMode] = useState(null);
    const [orderToEdit, setOrderToEdit] = useState(null);
    const [formData, setFormData] = useState({ ...EMPTY_FORM });
    const [eventDialog, setEventDialog] = useState({ open: false, item: null });
    const [automationDialog, setAutomationDialog] = useState({ open: false, order: null });
    const [batchAutomationOpen, setBatchAutomationOpen] = useState(false);
    const [eventForm, setEventForm] = useState({ ...EMPTY_EVENT_FORM });
    const [deleteTimers, setDeleteTimers] = useState({});
    const [eventDeleteTimers, setEventDeleteTimers] = useState({});
    const [bonusDeleteTimers, setBonusDeleteTimers] = useState({});
    const [savingBonusEvents, setSavingBonusEvents] = useState({});
    const [replacementAddStatus, setReplacementAddStatus] = useState({});
    const [refundAddStatus, setRefundAddStatus] = useState({});
    const [savingReplacementItems, setSavingReplacementItems] = useState({});
    const [savingRefundItems, setSavingRefundItems] = useState({});
    const [expandedItemEvents, setExpandedItemEvents] = useState(loadPersistedExpandedItemEvents);
    const [refundCandidateDialog, setRefundCandidateDialog] = useState({
        open: false,
        order: null,
        item: null,
        searchTerm: '',
        candidates: [],
        selectedItemId: null,
        isLoading: false,
    });
    const [repeatActionDialog, setRepeatActionDialog] = useState({
        open: false,
        type: null,
        order: null,
        item: null,
        previousDate: '',
        approvedEventType: null,
    });
    const [itemActionDates, setItemActionDates] = useState(loadPersistedItemActions);
    const [copiedTextKey, setCopiedTextKey] = useState(null);

    const selectedFolderConfig = SIDEBAR_FOLDERS.find(folder => folder.value === selectedFolder);
    const getFolderCount = (folder) => {
        if (folder.value === 'PROCESS') {
            return folderCounts.ORDERS_PROCESS || 0;
        }

        return folderCounts[folder.value] || 0;
    };

    const fetchOrders = useCallback(async () => {
        setIsLoading(true);
        const foldersToFetch = selectedFolderConfig?.folders || [selectedFolder];

        try {
            const responses = await Promise.all(foldersToFetch.map(folder => {
                const params = new URLSearchParams({
                    page: '0',
                    size: '200',
                    folder,
                    ...(searchTerm && { searchTerm }),
                });

                return axios.get(`${API_URL}/wob_orders/all?${params.toString()}`, { headers: requestHeaders });
            }));

            const nextOrders = responses.flatMap(response => response.data.content || []);
            setOrders(nextOrders);
        } catch (error) {
            console.error('Error fetching orders:', error);
        } finally {
            setIsLoading(false);
        }
    }, [requestHeaders, searchTerm, selectedFolder, selectedFolderConfig]);

    const fetchFolderCounts = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/wob_orders/counts`, { headers: requestHeaders });
            setFolderCounts(response.data || {});
        } catch (error) {
            console.error('Error fetching folder counts:', error);
        }
    }, [requestHeaders]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        fetchFolderCounts();
    }, [fetchFolderCounts]);

    useEffect(() => {
        try {
            localStorage.setItem(OPENED_ORDERS_STORAGE_KEY, JSON.stringify(openedOrders));
        } catch (error) {
            console.error('Error saving opened orders:', error);
        }
    }, [openedOrders]);

    useEffect(() => {
        try {
            localStorage.setItem(ORDER_ITEM_ACTIONS_STORAGE_KEY, JSON.stringify(itemActionDates));
        } catch (error) {
            console.error('Error saving order item actions:', error);
        }
    }, [itemActionDates]);

    useEffect(() => {
        try {
            localStorage.setItem(ORDER_EXPANDED_ITEM_EVENTS_STORAGE_KEY, JSON.stringify(expandedItemEvents));
        } catch (error) {
            console.error('Error saving expanded order item events:', error);
        }
    }, [expandedItemEvents]);

    useEffect(() => {
        const activeTimers = Object.entries(deleteTimers).filter(([, value]) => value > 0);
        if (activeTimers.length === 0) return;

        const timeout = setTimeout(() => {
            setDeleteTimers(current => {
                const next = {};
                Object.entries(current).forEach(([id, value]) => {
                    if (value > 1) {
                        next[id] = value - 1;
                    }
                });
                return next;
            });
        }, 1000);

        return () => clearTimeout(timeout);
    }, [deleteTimers]);

    useEffect(() => {
        const activeTimers = Object.entries(eventDeleteTimers).filter(([, value]) => value > 0);
        if (activeTimers.length === 0) return;

        const timeout = setTimeout(() => {
            setEventDeleteTimers(current => {
                const next = {};
                Object.entries(current).forEach(([id, value]) => {
                    if (value > 1) {
                        next[id] = value - 1;
                    }
                });
                return next;
            });
        }, 1000);

        return () => clearTimeout(timeout);
    }, [eventDeleteTimers]);

    useEffect(() => {
        const activeTimers = Object.entries(bonusDeleteTimers).filter(([, value]) => value > 0);
        if (activeTimers.length === 0) return;

        const timeout = setTimeout(() => {
            setBonusDeleteTimers(current => {
                const next = {};
                Object.entries(current).forEach(([id, value]) => {
                    if (value > 1) {
                        next[id] = value - 1;
                    }
                });
                return next;
            });
        }, 1000);

        return () => clearTimeout(timeout);
    }, [bonusDeleteTimers]);

    const openAddDialog = () => {
        setDialogMode('add');
        setOrderToEdit(null);
        setFormData({
            ...EMPTY_FORM,
        });
    };

    const openEditDialog = (order) => {
        setDialogMode('edit');
        setOrderToEdit(order);
        setFormData({
            email: order.email || '',
            folder: order.folder || selectedFolder,
            automationProfileNumber: order.automationProfileNumber == null
                ? ''
                : String(order.automationProfileNumber),
            note: order.note || '',
            rawContent: order.rawContent || '',
        });
    };

    const closeDialog = () => {
        setDialogMode(null);
        setOrderToEdit(null);
        setFormData({ ...EMPTY_FORM });
    };

    const openAutomationDialog = (order) => {
        setAutomationDialog({ open: true, order });
        refreshOrder(order.id);
    };

    const closeAutomationDialog = () => {
        setAutomationDialog({ open: false, order: null });
    };

    const refreshOrder = useCallback(async (orderId) => {
        try {
            const response = await axios.get(`${API_URL}/wob_orders/${orderId}`, {headers: requestHeaders});
            const refreshedOrder = response.data;
            setOrders(current => current.map(order => order.id === orderId ? refreshedOrder : order));
            setOpenedOrders(current => current.map(order => order.id === orderId ? refreshedOrder : order));
            setAutomationDialog(current => current.order?.id === orderId
                ? {...current, order: refreshedOrder}
                : current);
        } catch (error) {
            console.error('Error refreshing automated order:', error);
        }
    }, [requestHeaders]);

    const openEventDialog = (item, type) => {
        const existingEvent = item.events?.find(event => event.type === type);
        const defaultDateInputValue = toDateTimeInputValue();
        const hasExistingDate = Boolean(existingEvent?.eventDateText);
        const ticketEntries = TICKET_EVENT_TYPES.includes(type)
            ? parseTicketEntries(existingEvent)
            : [];
        const replacementTitleEntries = REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES.includes(type)
            ? parseReplacementTitleEntries(existingEvent)
            : [];
        const eventDateInputValue = hasExistingDate
            ? eventDateTextToDateInputValue(existingEvent.eventDateText)
            : existingEvent
                ? ''
                : defaultDateInputValue;

        setEventDialog({ open: true, item });
        setEventForm({
            ...EMPTY_EVENT_FORM,
            type,
            id: existingEvent?.id,
            eventDateText: hasExistingDate
                ? existingEvent.eventDateText
                : existingEvent
                    ? ''
                    : formatEventDateText(defaultDateInputValue),
            eventDateInputValue,
            ticketNumber: existingEvent?.ticketNumber || '',
            ticketEntries: ticketEntries.length > 0
                ? ticketEntries
                : TICKET_EVENT_TYPES.includes(type)
                    ? [createTicketEntry(eventDateInputValue || defaultDateInputValue, existingEvent?.ticketNumber || '')]
                    : [],
            replacementTitleEntries: replacementTitleEntries.length > 0
                ? replacementTitleEntries
                : REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES.includes(type)
                    ? [createReplacementTitleEntry(
                        eventDateInputValue || defaultDateInputValue,
                        existingEvent?.replacementTitle || '',
                        existingEvent?.eanIsbn || ''
                    )]
                    : [],
            replacementTitle: existingEvent?.replacementTitle || '',
            eanIsbn: existingEvent?.eanIsbn || '',
            agentName: existingEvent?.agentName || '',
            note: existingEvent?.note || '',
        });
    };

    const closeEventDialog = () => {
        setEventDialog({ open: false, item: null });
        setEventForm({ ...EMPTY_EVENT_FORM });
    };

    const getItemEvent = (item, type) => {
        return item.events?.find(event => event.type === type);
    };

    const itemHasEvents = (item, eventTypes) => {
        return eventTypes.every(type => Boolean(getItemEvent(item, type)));
    };

    const getOrderEventProgress = (order) => {
        const items = order.items || [];

        if (order.folder !== 'ORDERS_PROCESS') {
            return {
                completed: items.length,
                total: items.length,
                isProgress: false,
            };
        }

        return {
            completed: items.filter(item => itemHasEvents(
                item,
                getOrderEventTypes(item).map(eventType => eventType.type)
            )).length,
            total: items.length,
            isProgress: true,
        };
    };

    const getItemActionKey = (order, item) => {
        return String(item.id || `${order.id}-${item.displayOrder}`);
    };

    const isItemCompleteForFolder = (order, item) => {
        if (order.folder === 'ORDERS_PROCESS') {
            return Boolean(getItemEvent(item, REFUND_ISSUED_EVENT_TYPE));
        }

        return Boolean(item.isChecked);
    };

    const getEventProgressDateText = (event) => {
        if (!event) return '';

        if (TICKET_EVENT_TYPES.includes(event.type)) {
            const ticketEntries = parseTicketEntries(event);
            const lastTicketEntry = ticketEntries[ticketEntries.length - 1];
            return lastTicketEntry?.eventDateText || event.eventDateText || '';
        }

        return event.eventDateText || '';
    };

    const getItemEventProgressParts = (item) => {
        const itemEventTypes = getOrderEventTypes(item);
        const lastCompletedEvent = itemEventTypes.reduce((lastEvent, eventType, index) => {
            const event = getItemEvent(item, eventType.type);
            return event ? { index, type: eventType.type, label: eventType.label, eventDateText: getEventProgressDateText(event) } : lastEvent;
        }, -1);

        if (lastCompletedEvent === -1) {
            return {
                index: -1,
                type: null,
                label: `0/${itemEventTypes.length} - No events`,
                eventDate: '',
                eventDateText: '',
            };
        }

        return {
            index: lastCompletedEvent.index,
            type: lastCompletedEvent.type,
            label: `${lastCompletedEvent.index + 1}/${itemEventTypes.length} - ${lastCompletedEvent.label}`,
            eventDate: normalizeEventDateTextForDisplay(lastCompletedEvent.eventDateText),
            eventDateText: lastCompletedEvent.eventDateText,
        };
    };

    const shouldHighlightReplacementWait = (eventProgressParts) => {
        if (!APPROVED_REPLACEMENT_EVENT_TYPES.includes(eventProgressParts.type)) {
            return false;
        }

        const dateInputValue = eventDateTextToDateInputValue(eventProgressParts.eventDateText);
        if (!dateInputValue) return false;

        const eventDate = new Date(dateInputValue);
        if (Number.isNaN(eventDate.getTime())) return false;

        return Date.now() - eventDate.getTime() >= SIX_DAYS_IN_MS;
    };

    const shouldHighlightCaseWait = (eventProgressParts) => {
        if (!CASE_WAIT_EVENT_TYPES.includes(eventProgressParts.type)) {
            return false;
        }

        const dateInputValue = eventDateTextToDateInputValue(eventProgressParts.eventDateText);
        if (!dateInputValue) return false;

        const eventDate = new Date(dateInputValue);
        if (Number.isNaN(eventDate.getTime())) return false;

        return Date.now() - eventDate.getTime() >= TWENTY_HOURS_IN_MS;
    };

    const shouldHighlightIntermediateWait = (eventProgressParts) => {
        return INTERMEDIATE_WAIT_EVENT_TYPES.includes(eventProgressParts.type);
    };

    const getApprovedReplacementTitle = (item) => {
        return getItemEvent(item, BONUS_APPROVED_REPLACEMENT_EVENT_TYPE)?.replacementTitle
            || getItemEvent(item, STANDARD_APPROVED_REPLACEMENT_EVENT_TYPE)?.replacementTitle
            || '/';
    };

    const toggleItemEvents = (itemActionKey) => {
        setExpandedItemEvents(current => ({
            ...current,
            [itemActionKey]: !current[itemActionKey],
        }));
    };

    const markItemActionDate = (order, item, action) => {
        const actionKey = getItemActionKey(order, item);

        setItemActionDates(current => ({
            ...current,
            [actionKey]: {
                ...(current[actionKey] || {}),
                [action]: formatActionDate(),
            },
        }));
    };

    const copyText = async (text, key) => {
        if (!text) return;

        try {
            await navigator.clipboard.writeText(text);
            setCopiedTextKey(key);
            setTimeout(() => setCopiedTextKey(current => current === key ? null : current), 1500);
        } catch (error) {
            console.error('Error copying text:', error);
        }
    };

    const renderCopyButton = (value, copyKey, title) => {
        if (!value) return null;

        return (
            <button
                type="button"
                onClick={() => copyText(value, copyKey)}
                title={copiedTextKey === copyKey ? 'Copied' : title}
                className={`inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] font-medium transition-colors ${
                    copiedTextKey === copyKey
                        ? 'bg-green-50 text-green-700 dark:bg-green-400/10 dark:text-green-300'
                        : 'text-gray-400 hover:bg-gray-100 hover:text-cyan-800 dark:text-gray-500 dark:hover:bg-dark-800 dark:hover:text-panda-cyan'
                }`}
            >
                {copiedTextKey === copyKey ? (
                    <>
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        <span>Copied</span>
                    </>
                ) : (
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                )}
            </button>
        );
    };

    const renderEventField = (label, value, action = null, labelPrefix = '') => (
        <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-gray-700 dark:text-gray-300">
            <span>
                {labelPrefix && <span className="font-normal text-gray-700 dark:text-gray-300">{labelPrefix} </span>}
                <span className="font-semibold text-gray-800 dark:text-gray-200">{label}:</span>
            </span>
            <span className="min-w-0 truncate">{value || '/'}</span>
            {action}
        </div>
    );

    const renderEventDetails = (event, copyTicketKey, copyReplacementKey) => {
        if (!event) {
            return <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Not added</p>;
        }

        if (REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES.includes(event.type)) {
            const entriesToRender = parseReplacementTitleEntries(event);

            return (
                <div className="mt-1 space-y-1">
                    {(entriesToRender.length > 0
                        ? entriesToRender
                        : [{
                            eventDateText: event.eventDateText || '',
                            replacementTitle: event.replacementTitle || '',
                            eanIsbn: event.eanIsbn || '',
                        }]
                    ).map((entry, index) => {
                        const prefix = index === 0 ? '' : `(${index + 1})`;
                        const replacementCopyKey = index === 0 ? copyReplacementKey : `${copyReplacementKey}-${index + 1}`;

                        return (
                            <React.Fragment key={entry.id || `${entry.eventDateText}-${index}`}>
                                {renderEventField('Time', normalizeEventDateTextForDisplay(entry.eventDateText), null, prefix)}
                                {renderEventField(
                                    getEventTypeLabel(event.type),
                                    entry.replacementTitle,
                                    renderCopyButton(entry.replacementTitle, replacementCopyKey, 'Copy replacement title'),
                                    prefix
                                )}
                                {renderEventField('EAN/ISBN', entry.eanIsbn, null, prefix)}
                            </React.Fragment>
                        );
                    })}
                </div>
            );
        }

        if (TICKET_EVENT_TYPES.includes(event.type)) {
            const ticketEntries = parseTicketEntries(event);
            const entriesToRender = ticketEntries.length > 0
                ? ticketEntries
                : [{ eventDateText: event.eventDateText || '', ticketNumber: event.ticketNumber || '' }];

            return (
                <div className="mt-1 space-y-1">
                    {entriesToRender.map((entry, index) => {
                        const prefix = index === 0 ? '' : `(${index + 1})`;
                        const ticketCopyKey = index === 0 ? copyTicketKey : `${copyTicketKey}-${index + 1}`;

                        return (
                            <React.Fragment key={entry.id || `${entry.eventDateText}-${index}`}>
                                {renderEventField('Time', normalizeEventDateTextForDisplay(entry.eventDateText), null, prefix)}
                                {renderEventField(
                                    'Ticket Number',
                                    entry.ticketNumber,
                                    renderCopyButton(entry.ticketNumber, ticketCopyKey, 'Copy ticket number'),
                                    prefix
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            );
        }

        const timeRow = renderEventField('Time', normalizeEventDateTextForDisplay(event.eventDateText));

        if (APPROVED_REPLACEMENT_EVENT_TYPES.includes(event.type)) {
            return (
                <div className="mt-1 space-y-1">
                    {timeRow}
                    {renderEventField(
                        'Replacement Title',
                        event.replacementTitle,
                        renderCopyButton(event.replacementTitle, copyReplacementKey, 'Copy replacement title')
                    )}
                    {renderEventField('Agent', event.agentName)}
                    {event.note && (
                        <span title={event.note} className="inline-flex text-cyan-800 dark:text-panda-cyan">
                            <InformationCircleIcon className="h-4 w-4" />
                        </span>
                    )}
                </div>
            );
        }

        return <div className="mt-1 space-y-1">{timeRow}</div>;
    };

    const getReplacementActionKey = (item, approvedEventType) =>
        `${item.id}:${approvedEventType}`;

    const getReplacementActionName = (approvedEventType) =>
        approvedEventType === BONUS_APPROVED_REPLACEMENT_EVENT_TYPE
            ? 'replacementBonus'
            : 'replacement';

    const addReplacementToPurchasedItem = async (
        order,
        item,
        approvedEventType = STANDARD_APPROVED_REPLACEMENT_EVENT_TYPE,
        imageUrl = ''
    ) => {
        const replacementEvent = getItemEvent(item, approvedEventType);
        const replacementTitle = replacementEvent?.replacementTitle?.trim();
        const savingKey = getReplacementActionKey(item, approvedEventType);
        const actionName = getReplacementActionName(approvedEventType);

        if (!replacementTitle || savingReplacementItems[savingKey]) return;

        setSavingReplacementItems(current => ({ ...current, [savingKey]: true }));

        try {
            await axios.post(`${API_URL}/purchased_items/add`, {
                title: replacementTitle,
                author: '',
                itemCondition: 'Very Good',
                formatType: '',
                price: 0,
                priceFees: 0,
                shippingFees: 0,
                parcelForwardingFees: 0,
                expectedSellingPriceInRsd: 0,
                wobOrderNumber: 'FREE',
                status: 'DISPATCHED',
                purchasedDate: toLocalDateTimeInputValue(new Date(), true),
                imageUrl,
                type: 'BOOK',
                isDamaged: false,
                isReserved: false,
                paymentType: null,
            }, { headers: requestHeaders });

            setReplacementAddStatus(current => ({ ...current, [savingKey]: 'Replacement added' }));
            setTimeout(() => {
                setReplacementAddStatus(current => {
                    const next = { ...current };
                    delete next[savingKey];
                    return next;
                });
            }, 3000);
            markItemActionDate(order, item, actionName);
        } catch (error) {
            console.error('Error adding replacement to purchased item:', error);
            setReplacementAddStatus(current => ({ ...current, [savingKey]: 'Could not add replacement' }));
        } finally {
            setSavingReplacementItems(current => {
                const next = { ...current };
                delete next[savingKey];
                return next;
            });
        }
    };

    const closeRepeatActionDialog = () => {
        setRepeatActionDialog({
            open: false,
            type: null,
            order: null,
            item: null,
            previousDate: '',
            approvedEventType: null,
        });
    };

    const requestReplacementAdd = (
        order,
        item,
        approvedEventType = STANDARD_APPROVED_REPLACEMENT_EVENT_TYPE
    ) => {
        const actionDates = itemActionDates[getItemActionKey(order, item)] || {};
        const actionName = getReplacementActionName(approvedEventType);

        if (actionDates[actionName]) {
            setRepeatActionDialog({
                open: true,
                type: 'replacement',
                order,
                item,
                previousDate: actionDates[actionName],
                approvedEventType,
            });
            return;
        }

        addReplacementToPurchasedItem(order, item, approvedEventType);
    };

    const fetchRefundCandidates = useCallback(async (searchTerm) => {
        setRefundCandidateDialog(current => ({
            ...current,
            candidates: [],
            selectedItemId: null,
            isLoading: true,
        }));

        try {
            const params = new URLSearchParams({
                page: '0',
                size: '25',
                searchTerm: searchTerm || '',
                searchType: 'title',
                sortField: 'purchasedDate',
                sortDirection: 'desc',
            });
            const response = await axios.get(`${API_URL}/purchased_items/all?${params.toString()}`, { headers: requestHeaders });
            const candidates = response.data.content || [];

            setRefundCandidateDialog(current => ({
                ...current,
                candidates,
                selectedItemId: null,
                isLoading: false,
            }));
        } catch (error) {
            console.error('Error fetching refund candidates:', error);
            setRefundCandidateDialog(current => ({
                ...current,
                candidates: [],
                selectedItemId: null,
                isLoading: false,
            }));
        }
    }, [requestHeaders]);

    const openRefundCandidateDialog = (order, item) => {
        const refundEvent = getItemEvent(item, REFUND_ISSUED_EVENT_TYPE);
        const itemActionKey = getItemActionKey(order, item);

        if (!refundEvent || savingRefundItems[itemActionKey]) return;

        setRefundCandidateDialog({
            open: true,
            order,
            item,
            searchTerm: item.title || '',
            candidates: [],
            selectedItemId: null,
            isLoading: false,
        });
    };

    useEffect(() => {
        if (!refundCandidateDialog.open) return undefined;

        const searchTerm = refundCandidateDialog.searchTerm.trim();
        const timeoutId = setTimeout(() => {
            fetchRefundCandidates(searchTerm);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [fetchRefundCandidates, refundCandidateDialog.open, refundCandidateDialog.searchTerm]);

    const requestRefundAdd = (order, item) => {
        const actionDates = itemActionDates[getItemActionKey(order, item)] || {};

        if (actionDates.refund) {
            setRepeatActionDialog({
                open: true,
                type: 'refund',
                order,
                item,
                previousDate: actionDates.refund,
            });
            return;
        }

        openRefundCandidateDialog(order, item);
    };

    const confirmRepeatAction = () => {
        const { type, order, item, approvedEventType } = repeatActionDialog;
        closeRepeatActionDialog();

        if (!order || !item) return;

        if (type === 'replacement') {
            addReplacementToPurchasedItem(
                order,
                item,
                approvedEventType || STANDARD_APPROVED_REPLACEMENT_EVENT_TYPE
            );
            return;
        }

        if (type === 'refund') {
            openRefundCandidateDialog(order, item);
        }
    };

    const closeRefundCandidateDialog = () => {
        setRefundCandidateDialog({
            open: false,
            order: null,
            item: null,
            searchTerm: '',
            candidates: [],
            selectedItemId: null,
            isLoading: false,
        });
    };

    const addRefundFromSelectedItem = async () => {
        const { order, item, candidates, selectedItemId } = refundCandidateDialog;
        if (!order || !item || !selectedItemId) return;

        const selectedItem = candidates.find(candidate => candidate.id === selectedItemId);
        if (!selectedItem) return;

        const itemActionKey = getItemActionKey(order, item);

        setSavingRefundItems(current => ({ ...current, [itemActionKey]: true }));

        try {
            await axios.post(`${API_URL}/refunded_items/add`, {
                refundDate: toLocalDateTimeInputValue(new Date(), true),
                paidPrice: Number.isFinite(item.priceGbp) ? item.priceGbp : 0,
                refundedAmount: 0,
                status: 'ISSUED',
                purchasedItem: selectedItem,
            }, { headers: requestHeaders });

            setRefundAddStatus(current => ({ ...current, [itemActionKey]: 'Refund added' }));
            markItemActionDate(order, item, 'refund');
            closeRefundCandidateDialog();
            setTimeout(() => {
                setRefundAddStatus(current => {
                    const next = { ...current };
                    delete next[itemActionKey];
                    return next;
                });
            }, 3000);
        } catch (error) {
            console.error('Error adding refund:', error);
            setRefundAddStatus(current => ({ ...current, [itemActionKey]: 'Could not add refund' }));
        } finally {
            setSavingRefundItems(current => {
                const next = { ...current };
                delete next[itemActionKey];
                return next;
            });
        }
    };

    const saveOrder = async () => {
        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                email: formData.email.trim(),
                automationProfileNumber: formData.automationProfileNumber === ''
                    ? null
                    : Number.parseInt(formData.automationProfileNumber, 10),
                note: formData.note.trim(),
                rawContent: formData.rawContent.trim(),
            };

            const response = dialogMode === 'edit' && orderToEdit
                ? await axios.put(`${API_URL}/wob_orders/edit/${orderToEdit.id}`, payload, { headers: requestHeaders })
                : await axios.post(`${API_URL}/wob_orders/add`, payload, { headers: requestHeaders });

            const savedOrder = response.data;
            setOpenedOrders(current => current.map(order => order.id === savedOrder.id ? savedOrder : order));
            closeDialog();
            await fetchOrders();
            await fetchFolderCounts();
        } catch (error) {
            console.error('Error saving order:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const moveOrder = async (orderId, folder) => {
        try {
            await axios.put(`${API_URL}/wob_orders/move/${orderId}?folder=${folder}`, {}, { headers: requestHeaders });
            setOrders(current => current.filter(order => order.id !== orderId));
            setOpenedOrders(current => current.map(order => order.id === orderId ? { ...order, folder } : order));
            await fetchFolderCounts();
        } catch (error) {
            console.error('Error moving order:', error);
        }
    };

    const syncTicketEntry = (index, changes) => {
        setEventForm(current => {
            const ticketEntries = current.ticketEntries.length > 0
                ? current.ticketEntries
                : [createTicketEntry(current.eventDateInputValue || toDateTimeInputValue(), current.ticketNumber)];
            const nextTicketEntries = ticketEntries.map((entry, entryIndex) => (
                entryIndex === index ? { ...entry, ...changes } : entry
            ));

            return {
                ...current,
                ticketEntries: nextTicketEntries,
            };
        });
    };

    const addTicketEntry = () => {
        setEventForm(current => ({
            ...current,
            ticketEntries: [
                ...(current.ticketEntries.length > 0
                    ? current.ticketEntries
                    : [createTicketEntry(current.eventDateInputValue || toDateTimeInputValue(), current.ticketNumber)]),
                createTicketEntry(),
            ],
        }));
    };

    const removeTicketEntry = (entryId) => {
        setEventForm(current => ({
            ...current,
            ticketEntries: current.ticketEntries.filter(entry => entry.id !== entryId),
        }));
    };

    const syncReplacementTitleEntry = (index, changes) => {
        setEventForm(current => {
            const replacementTitleEntries = current.replacementTitleEntries.length > 0
                ? current.replacementTitleEntries
                : [createReplacementTitleEntry(
                    current.eventDateInputValue || toDateTimeInputValue(),
                    current.replacementTitle,
                    current.eanIsbn
                )];
            const nextReplacementTitleEntries = replacementTitleEntries.map((entry, entryIndex) => (
                entryIndex === index ? { ...entry, ...changes } : entry
            ));

            return {
                ...current,
                replacementTitleEntries: nextReplacementTitleEntries,
            };
        });
    };

    const addReplacementTitleEntry = () => {
        setEventForm(current => ({
            ...current,
            replacementTitleEntries: [
                ...(current.replacementTitleEntries.length > 0
                    ? current.replacementTitleEntries
                    : [createReplacementTitleEntry(
                        current.eventDateInputValue || toDateTimeInputValue(),
                        current.replacementTitle,
                        current.eanIsbn
                    )]),
                createReplacementTitleEntry(),
            ],
        }));
    };

    const removeReplacementTitleEntry = (entryId) => {
        setEventForm(current => ({
            ...current,
            replacementTitleEntries: current.replacementTitleEntries.filter(entry => entry.id !== entryId),
        }));
    };

    const saveItemEvent = async () => {
        if (!eventDialog.item) return;

        try {
            const ticketEntries = TICKET_EVENT_TYPES.includes(eventForm.type)
                ? (eventForm.ticketEntries.length > 0
                    ? eventForm.ticketEntries
                    : [createTicketEntry(eventForm.eventDateInputValue, eventForm.ticketNumber)])
                    .map((entry, index) => ({
                        ...entry,
                        eventDateText: (index === 0 ? eventForm.eventDateText : entry.eventDateText).trim(),
                        ticketNumber: (index === 0 ? eventForm.ticketNumber : entry.ticketNumber).trim(),
                    }))
                    .filter(entry => entry.eventDateText || entry.ticketNumber)
                : [];
            const firstTicketEntry = ticketEntries[0];
            const replacementTitleEntries = REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES.includes(eventForm.type)
                ? (eventForm.replacementTitleEntries.length > 0
                    ? eventForm.replacementTitleEntries
                    : [createReplacementTitleEntry(
                        eventForm.eventDateInputValue,
                        eventForm.replacementTitle,
                        eventForm.eanIsbn
                    )])
                    .map((entry) => ({
                        ...entry,
                        eventDateText: entry.eventDateText.trim(),
                        replacementTitle: entry.replacementTitle.trim(),
                        eanIsbn: entry.eanIsbn.trim(),
                    }))
                    .filter(entry => entry.eventDateText || entry.replacementTitle || entry.eanIsbn)
                : [];
            const firstReplacementTitleEntry = replacementTitleEntries[0];
            const payload = {
                ...eventForm,
                ticketNumber: firstTicketEntry ? firstTicketEntry.ticketNumber : '',
                ticketEntriesText: TICKET_EVENT_TYPES.includes(eventForm.type)
                    ? serializeTicketEntries(ticketEntries)
                    : REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES.includes(eventForm.type)
                        ? serializeReplacementTitleEntries(replacementTitleEntries)
                        : '',
                replacementTitle: REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES.includes(eventForm.type)
                    ? (firstReplacementTitleEntry?.replacementTitle || '')
                    : APPROVED_REPLACEMENT_EVENT_TYPES.includes(eventForm.type)
                        ? eventForm.replacementTitle.trim()
                        : '',
                eanIsbn: REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES.includes(eventForm.type)
                    ? (firstReplacementTitleEntry?.eanIsbn || '')
                    : '',
                eventDateText: firstTicketEntry
                    ? firstTicketEntry.eventDateText
                    : firstReplacementTitleEntry
                        ? firstReplacementTitleEntry.eventDateText
                        : eventForm.eventDateText.trim(),
                agentName: APPROVED_REPLACEMENT_EVENT_TYPES.includes(eventForm.type)
                        ? eventForm.agentName.trim()
                        : '',
                note: APPROVED_REPLACEMENT_EVENT_TYPES.includes(eventForm.type)
                        ? eventForm.note.trim()
                        : '',
            };
            const response = await axios.post(
                `${API_URL}/wob_orders/items/${eventDialog.item.id}/events`,
                payload,
                { headers: requestHeaders }
            );
            const updatedOrder = response.data;

            setOpenedOrders(current => current.map(order => order.id === updatedOrder.id ? updatedOrder : order));
            setOrders(current => current.map(order => order.id === updatedOrder.id ? updatedOrder : order));
            closeEventDialog();
        } catch (error) {
            console.error('Error saving item event:', error);
        }
    };

    const isEventSaveDisabled = () => {
        if (REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES.includes(eventForm.type)) {
            const replacementTitleEntries = eventForm.replacementTitleEntries.length > 0
                ? eventForm.replacementTitleEntries
                : [createReplacementTitleEntry(
                    eventForm.eventDateInputValue,
                    eventForm.replacementTitle,
                    eventForm.eanIsbn
                )];

            return !replacementTitleEntries.some(entry => entry.replacementTitle.trim());
        }

        if (APPROVED_REPLACEMENT_EVENT_TYPES.includes(eventForm.type)) {
            return !eventForm.replacementTitle.trim();
        }

        return false;
    };

    const deleteItemEvent = async (eventId) => {
        if (!eventId) return;

        if (!eventDeleteTimers[eventId]) {
            setEventDeleteTimers(current => ({ ...current, [eventId]: 3 }));
            return;
        }

        try {
            const response = await axios.delete(`${API_URL}/wob_orders/events/${eventId}`, { headers: requestHeaders });
            const updatedOrder = response.data;

            setOpenedOrders(current => current.map(order => order.id === updatedOrder.id ? updatedOrder : order));
            setOrders(current => current.map(order => order.id === updatedOrder.id ? updatedOrder : order));
            setEventDeleteTimers(current => {
                const next = { ...current };
                delete next[eventId];
                return next;
            });
            closeEventDialog();
        } catch (error) {
            console.error('Error deleting item event:', error);
        }
    };

    const updateBonusEvents = async (item, enabled) => {
        if (!item?.id || savingBonusEvents[item.id]) return;

        if (!enabled && !bonusDeleteTimers[item.id]) {
            setBonusDeleteTimers(current => ({ ...current, [item.id]: 3 }));
            return;
        }

        setSavingBonusEvents(current => ({ ...current, [item.id]: true }));
        try {
            const response = await axios.put(
                `${API_URL}/wob_orders/items/${item.id}/bonus-events?enabled=${enabled}`,
                null,
                { headers: requestHeaders }
            );
            const updatedOrder = response.data;

            setOpenedOrders(current => current.map(order => order.id === updatedOrder.id ? updatedOrder : order));
            setOrders(current => current.map(order => order.id === updatedOrder.id ? updatedOrder : order));
            setBonusDeleteTimers(current => {
                const next = { ...current };
                delete next[item.id];
                return next;
            });
        } catch (error) {
            console.error(`Error ${enabled ? 'enabling' : 'removing'} bonus events:`, error);
        } finally {
            setSavingBonusEvents(current => {
                const next = { ...current };
                delete next[item.id];
                return next;
            });
        }
    };

    const handleDeleteClick = async (orderId) => {
        if (!deleteTimers[orderId]) {
            setDeleteTimers(current => ({ ...current, [orderId]: 3 }));
            return;
        }

        try {
            await axios.delete(`${API_URL}/wob_orders/delete/${orderId}`, { headers: requestHeaders });
            setOrders(current => current.filter(order => order.id !== orderId));
            setOpenedOrders(current => current.filter(order => order.id !== orderId));
            setDeleteTimers(current => {
                const next = { ...current };
                delete next[orderId];
                return next;
            });
            await fetchFolderCounts();
        } catch (error) {
            console.error('Error deleting order:', error);
        }
    };

    const openOrder = (order) => {
        setOpenedOrders([order]);
    };

    const closeOrder = (orderId) => {
        setOpenedOrders(current => current.filter(order => order.id !== orderId));
    };

    const renderOrderSummary = (order) => {
        const progress = getOrderEventProgress(order);
        const emailCopyKey = `summary-email-${order.id}`;
        const orderNotePreview = truncateText(order.note);

        return (
        <button
            key={order.id}
            type="button"
            onClick={() => openOrder(order)}
            className={`w-full text-left rounded-lg border bg-white dark:bg-dark-900 p-3 shadow-sm hover:border-cyan-500 dark:hover:border-panda-cyan transition-colors ${
                openedOrders[0]?.id === order.id
                    ? 'border-cyan-700 dark:border-panda-cyan/60'
                    : 'border-gray-200 dark:border-dark-700'
            }`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1">
                        <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{order.email}</p>
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                copyText(order.email, emailCopyKey);
                            }}
                            title={copiedTextKey === emailCopyKey ? 'Copied' : 'Copy email'}
                            className={`inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[11px] font-medium transition-colors ${
                                copiedTextKey === emailCopyKey
                                    ? 'bg-green-50 text-green-700 dark:bg-green-400/10 dark:text-green-300'
                                    : 'text-gray-400 hover:bg-gray-100 hover:text-cyan-800 dark:text-gray-500 dark:hover:bg-dark-800 dark:hover:text-panda-cyan'
                            }`}
                        >
                            {copiedTextKey === emailCopyKey ? (
                                <>
                                    <CheckCircleIcon className="h-3.5 w-3.5" />
                                    <span>Copied</span>
                                </>
                            ) : (
                                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{order.wobOrderNumber || 'No WOB number'}</p>
                </div>
                <span className="shrink-0 rounded-md bg-cyan-50 dark:bg-panda-cyan/10 px-2 py-1 text-xs font-medium text-cyan-900 dark:text-panda-cyan">
                    {progress.isProgress ? `${progress.completed}/${progress.total}` : progress.total}
                </span>
            </div>

            <div className="mt-3 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                <span>{order.purchaseDateText || '/'}</span>
                <span>{formatCurrency(order.totalPriceGbp)}</span>
            </div>
            {orderNotePreview && (
                <p title={order.note} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Note: {orderNotePreview}
                </p>
            )}

            <div className="mt-3 flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
                <select
                    value={order.folder}
                    onChange={(event) => moveOrder(order.id, event.target.value)}
                    className="min-w-0 flex-1 rounded-md border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 px-2 py-1.5 text-xs text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-700"
                >
                    {FOLDERS.map(folder => (
                        <option key={folder.value} value={folder.value}>{folder.shortLabel}</option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={() => openEditDialog(order)}
                    className="rounded-md border border-gray-200 dark:border-dark-700 p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-800"
                >
                    <PencilSquareIcon className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => handleDeleteClick(order.id)}
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border text-xs font-semibold transition-colors ${
                        deleteTimers[order.id]
                            ? 'border-red-300 bg-red-50 text-red-800 dark:border-panda-pink/40 dark:bg-panda-pink/10 dark:text-panda-pink'
                            : 'border-red-100 text-red-700 hover:bg-red-50 dark:border-panda-pink/30 dark:text-panda-pink dark:hover:bg-panda-pink/10'
                    }`}
                >
                    {deleteTimers[order.id] ? deleteTimers[order.id] : <TrashIcon className="h-4 w-4" />}
                </button>
            </div>
        </button>
        );
    };

    const renderOpenedOrder = (order) => {
        const folderStyle = FOLDER_STYLES[order.folder] || FOLDER_STYLES.ORDERS_PROCESS;

        return (
        <div
            key={order.id}
            className="flex h-full min-w-0 flex-col rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-900"
        >
            <div
                className="orders-folder-header flex items-start justify-between gap-3 border-b p-4 transition-colors"
                style={{
                    '--folder-bg-light': folderStyle.backgroundLight,
                    '--folder-bg-hover-light': folderStyle.backgroundHoverLight,
                    '--folder-border-light': folderStyle.borderLight,
                    '--folder-bg-dark': folderStyle.backgroundDark,
                    '--folder-bg-hover-dark': folderStyle.backgroundHoverDark,
                    '--folder-border-dark': folderStyle.borderDark,
                }}
            >
                <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1">
                        <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{order.email}</p>
                        <button
                            type="button"
                            onClick={(event) => {
                                event.stopPropagation();
                                copyText(order.email, `email-${order.id}`);
                            }}
                            title={copiedTextKey === `email-${order.id}` ? 'Copied' : 'Copy email'}
                            className={`inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[11px] font-medium transition-colors ${
                                copiedTextKey === `email-${order.id}`
                                    ? 'bg-green-50 text-green-700 dark:bg-green-400/10 dark:text-green-300'
                                    : 'text-gray-400 hover:bg-white/70 hover:text-cyan-800 dark:text-gray-500 dark:hover:bg-dark-800 dark:hover:text-panda-cyan'
                            }`}
                        >
                            {copiedTextKey === `email-${order.id}` ? (
                                <>
                                    <CheckCircleIcon className="h-3.5 w-3.5" />
                                    <span>Copied</span>
                                </>
                            ) : (
                                <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                            )}
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{order.wobOrderNumber || 'No WOB number'} · {order.purchaseDateText || '/'} · {formatCurrency(order.totalPriceGbp)}</p>
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                        Profile number: {order.automationProfileNumber == null
                            ? '/'
                            : String(order.automationProfileNumber).padStart(2, '0')}
                    </p>
                    <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">Note: {order.note || '/'}</p>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => openAutomationDialog(order)}
                        className="rounded-md p-1.5 text-cyan-800 hover:bg-cyan-100 dark:text-panda-cyan dark:hover:bg-panda-cyan/10"
                        title="WoB automation"
                    >
                        <BoltIcon className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => openEditDialog(order)}
                        className="rounded-md p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800"
                    >
                        <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => closeOrder(order.id)}
                        className="rounded-md p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
                <div className="space-y-3">
                    {(order.items || []).map(item => {
                        const itemActionKey = getItemActionKey(order, item);
                        const actionDates = itemActionDates[itemActionKey] || {};
                        const isItemComplete = isItemCompleteForFolder(order, item);
                        const areEventsExpanded = Boolean(expandedItemEvents[itemActionKey]);
                        const eventProgressParts = getItemEventProgressParts(item);
                        const shouldHighlightItem = shouldHighlightReplacementWait(eventProgressParts);
                        const shouldStripeHighlightItem = shouldHighlightCaseWait(eventProgressParts);
                        const shouldOrangeHighlightItem = shouldHighlightIntermediateWait(eventProgressParts);

                        return (
                        <div
                            key={item.id || `${order.id}-${item.displayOrder}`}
                            className={`rounded-lg border p-3 ${
                                shouldHighlightItem
                                    ? 'border-red-100 bg-red-50/70 dark:border-panda-pink/35 dark:bg-panda-pink/10 dark:shadow-[inset_3px_0_0_rgba(247,118,142,0.35)]'
                                    : shouldStripeHighlightItem
                                        ? 'orders-case-wait-stripes border-red-100 dark:border-panda-pink/35 dark:bg-dark-850'
                                    : shouldOrangeHighlightItem
                                        ? 'border-orange-100 bg-orange-50/70 dark:border-amber-400/30 dark:bg-amber-400/10'
                                    : 'border-gray-100 bg-gray-50 dark:border-dark-700 dark:bg-dark-850'
                            }`}
                        >
                            <div className="flex items-start gap-3">
                                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-[4px] border ${
                                    isItemComplete
                                        ? 'border-green-500 bg-green-500 text-white'
                                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-900'
                                }`}>
                                    {isItemComplete && <CheckIcon className="h-4 w-4 stroke-[3]" />}
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                                        <div className="flex min-w-0 items-start gap-1">
                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.title}</p>
                                            <button
                                                type="button"
                                                onClick={() => copyText(item.title, `item-${item.id}`)}
                                                title={copiedTextKey === `item-${item.id}` ? 'Copied' : 'Copy title'}
                                                className={`mt-0.5 inline-flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[11px] font-medium transition-colors ${
                                                    copiedTextKey === `item-${item.id}`
                                                        ? 'bg-green-50 text-green-700 dark:bg-green-400/10 dark:text-green-300'
                                                        : 'text-gray-400 hover:bg-gray-100 hover:text-cyan-800 dark:text-gray-500 dark:hover:bg-dark-800 dark:hover:text-panda-cyan'
                                                }`}
                                            >
                                                {copiedTextKey === `item-${item.id}` ? (
                                                    <>
                                                        <CheckCircleIcon className="h-3.5 w-3.5" />
                                                        <span>Copied</span>
                                                    </>
                                                ) : (
                                                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => toggleItemEvents(itemActionKey)}
                                                title={areEventsExpanded ? 'Hide events' : 'Show events'}
                                                className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-cyan-800 dark:text-gray-400 dark:hover:bg-dark-800 dark:hover:text-panda-cyan"
                                            >
                                                {areEventsExpanded ? (
                                                    <ChevronDownIcon className="h-4 w-4" />
                                                ) : (
                                                    <ChevronRightIcon className="h-4 w-4" />
                                                )}
                                            </button>
                                        </div>
                                        <p className="shrink-0 text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(item.priceGbp)}</p>
                                    </div>
                                    <div className="mt-1 space-y-0.5">
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Replacement: {getApprovedReplacementTitle(item)}</p>
                                        <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                                            {eventProgressParts.label}
                                            {eventProgressParts.eventDate && (
                                                <>
                                                    {' - '}
                                                    <span className="font-normal text-purple-700 dark:text-purple-300">
                                                        {eventProgressParts.eventDate}
                                                    </span>
                                                </>
                                            )}
                                        </p>
                                    </div>

                                    {areEventsExpanded && (
                                    <>
                                    <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-900">
                                        {getOrderEventTypes(item).map((action, index) => {
                                            const event = getItemEvent(item, action.type);
                                            const copyTicketKey = event ? `ticket-${event.id}` : '';
                                            const copyReplacementKey = event ? `replacement-${event.id}` : '';
                                            const isBlueEvent = BLUE_EVENT_TYPES.includes(action.type);
                                            const isYellowEvent = YELLOW_EVENT_TYPES.includes(action.type);
                                            const eventRowClass = !event
                                                ? 'bg-white dark:bg-dark-900'
                                                : isYellowEvent
                                                ? 'bg-pink-50/90 dark:bg-panda-pink/10'
                                                : isBlueEvent
                                                    ? 'bg-cyan-50/80 dark:bg-panda-cyan/10'
                                                    : 'bg-white dark:bg-dark-900';
                                            const eventBorderClass = !event
                                                ? 'border-gray-100 dark:border-dark-700'
                                                : isYellowEvent
                                                ? 'border-pink-100 dark:border-panda-pink/20'
                                                : 'border-cyan-100 dark:border-panda-cyan/20';
                                            const eventDotClass = !event
                                                ? 'bg-gray-200 ring-gray-100 dark:bg-dark-700 dark:ring-dark-800'
                                                : isYellowEvent
                                                ? 'bg-pink-400 ring-pink-200 dark:bg-panda-pink dark:ring-panda-pink/30'
                                                : 'bg-cyan-500 ring-cyan-200 dark:bg-panda-cyan dark:ring-panda-cyan/30';
                                            const eventNameClass = !event
                                                ? 'text-gray-500 dark:text-gray-400'
                                                : isYellowEvent
                                                ? 'text-pink-950 dark:text-panda-pink'
                                                : 'text-cyan-950 dark:text-panda-cyan';
                                            const isReplacementActionRow = APPROVED_REPLACEMENT_EVENT_TYPES.includes(action.type);
                                            const isRefundActionRow = action.type === REFUND_ISSUED_EVENT_TYPE;
                                            const isBonusToggleRow = action.type === BONUS_TOGGLE_EVENT_TYPE;
                                            const replacementSavingKey = isReplacementActionRow
                                                ? getReplacementActionKey(item, action.type)
                                                : '';
                                            const replacementActionName = isReplacementActionRow
                                                ? getReplacementActionName(action.type)
                                                : '';

                                            return (
                                                <div
                                                    key={action.type}
                                                    className={`flex items-start gap-3 px-3 py-2.5 ${eventRowClass} ${
                                                        index > 0 ? `border-t ${eventBorderClass}` : ''
                                                    }`}
                                                >
                                                    <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ring-2 ${eventDotClass}`} />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                                                            <p className={`text-xs font-semibold ${eventNameClass}`}>
                                                                {action.label}
                                                            </p>
                                                            <div className="flex shrink-0 items-center gap-2">
                                                                {isReplacementActionRow && (
                                                                    <button
                                                                        type="button"
                                                                        disabled={!event || savingReplacementItems[replacementSavingKey]}
                                                                        onClick={() => requestReplacementAdd(order, item, action.type)}
                                                                        title={event ? 'Add replacement title' : 'Add a Replacement Title Approved event first'}
                                                                        className={`h-7 rounded-md border px-2 text-xs font-medium transition-colors ${
                                                                            event && !savingReplacementItems[replacementSavingKey]
                                                                                ? 'border-green-300 bg-green-50 text-green-900 hover:bg-green-100 dark:border-green-400/40 dark:bg-green-400/10 dark:text-green-300'
                                                                                : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-dark-700 dark:bg-dark-800 dark:text-gray-600'
                                                                        }`}
                                                                    >
                                                                        {savingReplacementItems[replacementSavingKey] ? 'Adding...' : 'Add replacement'}
                                                                    </button>
                                                                )}
                                                                {isRefundActionRow && (
                                                                    <button
                                                                        type="button"
                                                                        disabled={!event || savingRefundItems[itemActionKey]}
                                                                        onClick={() => requestRefundAdd(order, item)}
                                                                        title={event ? 'Add refund' : 'Add a Refund Issued event first'}
                                                                        className={`h-7 rounded-md border px-2 text-xs font-medium transition-colors ${
                                                                            event && !savingRefundItems[itemActionKey]
                                                                                ? 'border-rose-300 bg-rose-50 text-rose-900 hover:bg-rose-100 dark:border-rose-400/40 dark:bg-rose-400/10 dark:text-rose-300'
                                                                                : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-dark-700 dark:bg-dark-800 dark:text-gray-600'
                                                                        }`}
                                                                    >
                                                                        {savingRefundItems[itemActionKey] ? 'Adding...' : 'Add refund'}
                                                                    </button>
                                                                )}
                                                                {isBonusToggleRow && (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => updateBonusEvents(item, true)}
                                                                            disabled={Boolean(item.bonusEventsEnabled) || savingBonusEvents[item.id]}
                                                                            title={item.bonusEventsEnabled ? 'Bonus events are enabled' : 'Add bonus events'}
                                                                            className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition-colors ${
                                                                                !item.bonusEventsEnabled && !savingBonusEvents[item.id]
                                                                                    ? 'border-green-200 text-green-700 hover:bg-green-50 dark:border-green-400/30 dark:text-green-300 dark:hover:bg-green-400/10'
                                                                                    : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-dark-700 dark:bg-dark-800 dark:text-gray-600'
                                                                            }`}
                                                                        >
                                                                            <PlusCircleIcon className="h-4 w-4" />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => updateBonusEvents(item, false)}
                                                                            disabled={!item.bonusEventsEnabled || savingBonusEvents[item.id]}
                                                                            title={
                                                                                item.bonusEventsEnabled
                                                                                    ? 'Click twice within 3 seconds to remove only bonus events'
                                                                                    : 'Enable bonus events first'
                                                                            }
                                                                            className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-xs font-semibold transition-colors ${
                                                                                item.bonusEventsEnabled && !savingBonusEvents[item.id]
                                                                                    ? bonusDeleteTimers[item.id]
                                                                                        ? 'border-red-300 bg-red-50 text-red-800 dark:border-panda-pink/40 dark:bg-panda-pink/10 dark:text-panda-pink'
                                                                                        : 'border-red-200 text-red-700 hover:bg-red-50 dark:border-panda-pink/30 dark:text-panda-pink dark:hover:bg-panda-pink/10'
                                                                                    : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 dark:border-dark-700 dark:bg-dark-800 dark:text-gray-600'
                                                                            }`}
                                                                        >
                                                                            {bonusDeleteTimers[item.id]
                                                                                ? bonusDeleteTimers[item.id]
                                                                                : <MinusCircleIcon className="h-4 w-4" />}
                                                                        </button>
                                                                    </>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openEventDialog(item, action.type)}
                                                                    className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition-colors ${
                                                                        !event
                                                                            ? 'border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-dark-700 dark:text-gray-400 dark:hover:bg-dark-800'
                                                                            : isYellowEvent
                                                                            ? 'border-pink-200 bg-white/70 text-pink-900 hover:bg-pink-100 dark:border-panda-pink/30 dark:bg-dark-900/30 dark:text-panda-pink dark:hover:bg-panda-pink/10'
                                                                            : 'border-cyan-200 bg-white/70 text-cyan-900 hover:bg-cyan-100 dark:border-panda-cyan/30 dark:bg-dark-900/30 dark:text-panda-cyan dark:hover:bg-panda-cyan/10'
                                                                    }`}
                                                                    title={event ? `Edit ${action.label}` : `Add ${action.label}`}
                                                                >
                                                                    <PencilSquareIcon className="h-4 w-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {renderEventDetails(event, copyTicketKey, copyReplacementKey)}
                                                        {isReplacementActionRow && actionDates[replacementActionName] && (
                                                            <span className="mt-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                                                                Added {actionDates[replacementActionName]}
                                                            </span>
                                                        )}
                                                        {isReplacementActionRow && replacementAddStatus[replacementSavingKey] && (
                                                            <span
                                                                title={replacementAddStatus[replacementSavingKey]}
                                                                className={`mt-1 block text-xs font-medium ${
                                                                    replacementAddStatus[replacementSavingKey].startsWith('Could')
                                                                        ? 'text-red-700 dark:text-panda-pink'
                                                                        : 'text-green-700 dark:text-green-300'
                                                                }`}
                                                            >
                                                                {replacementAddStatus[replacementSavingKey]}
                                                            </span>
                                                        )}
                                                        {isRefundActionRow && actionDates.refund && (
                                                            <span className="mt-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                                                                Added {actionDates.refund}
                                                            </span>
                                                        )}
                                                        {isRefundActionRow && refundAddStatus[itemActionKey] && (
                                                            <span
                                                                title={refundAddStatus[itemActionKey]}
                                                                className={`mt-1 block text-xs font-medium ${
                                                                    refundAddStatus[itemActionKey].startsWith('Could')
                                                                        ? 'text-red-700 dark:text-panda-pink'
                                                                        : 'text-rose-700 dark:text-rose-300'
                                                                }`}
                                                            >
                                                                {refundAddStatus[itemActionKey]}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    </>
                                    )}
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            </div>
        </div>
        );
    };

    return (
        <div className="flex h-full min-h-0 flex-col gap-4">
            <div className="rounded-lg bg-white dark:bg-dark-900 shadow-lg p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <Typography variant="h4" className="text-gray-900 dark:text-gray-100">Orders</Typography>
                        <div className="flex flex-wrap items-center gap-2">
                            {SIDEBAR_FOLDERS.map(folder => {
                                const folderStyleKey = folder.value === 'PROCESS' ? 'ORDERS_PROCESS' : folder.value;

                                return (
                                    <button
                                        key={folder.value}
                                        type="button"
                                        onClick={() => setSelectedFolder(folder.value)}
                                        className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                                            selectedFolder === folder.value
                                                ? 'border-cyan-700 bg-cyan-50 text-cyan-950 dark:border-panda-cyan/40 dark:bg-panda-cyan/10 dark:text-panda-cyan'
                                                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-dark-700 dark:bg-dark-900 dark:text-gray-300 dark:hover:bg-dark-800'
                                        }`}
                                    >
                                        <span className="flex min-w-0 items-center gap-2">
                                            <FolderSolidIcon
                                                className="orders-folder-icon h-4 w-4 shrink-0"
                                                style={{
                                                    '--folder-icon-light': FOLDER_STYLES[folderStyleKey]?.iconLight,
                                                    '--folder-icon-dark': FOLDER_STYLES[folderStyleKey]?.iconDark,
                                                }}
                                            />
                                            <span className="truncate">{folder.shortLabel}</span>
                                        </span>
                                        <span className="shrink-0 rounded-md bg-gray-100 dark:bg-dark-800 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300">
                                            {getFolderCount(folder)}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <Input
                            label="Search"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            className="dark:text-gray-100"
                            labelProps={{ className: "dark:text-gray-400" }}
                        />
                        <button
                            onClick={openAddDialog}
                            className="mv-primary-button"
                        >
                            <PlusCircleIcon className="w-4 h-4" />
                            <span className="hidden md:inline">Add</span>
                        </button>
                        {selectedFolder === 'PROCESS' && (
                            <button
                                onClick={() => setBatchAutomationOpen(true)}
                                className="mv-primary-button"
                                title="Run automation for selected Process profiles"
                            >
                                <BoltIcon className="w-4 h-4" />
                                <span className="hidden md:inline">Automation</span>
                            </button>
                        )}
                        <button
                            onClick={() => { fetchOrders(); fetchFolderCounts(); }}
                            className="mv-secondary-button"
                        >
                            <ArrowPathIcon className="w-4 h-4" />
                            <span className="hidden md:inline">Refresh</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden xl:grid-cols-[320px_minmax(0,1fr)] 2xl:grid-cols-[340px_minmax(0,1fr)]">
                <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg bg-white shadow-lg dark:bg-dark-900 xl:h-full">
                    <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 p-3">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selectedFolderConfig?.shortLabel || selectedFolderConfig?.label}</p>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{orders.length}</span>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto p-3">
                        {isLoading ? (
                            <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Loading...</div>
                        ) : orders.length === 0 ? (
                            <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">No orders in this folder.</div>
                        ) : (
                            <div className="space-y-2">
                                {orders.map(renderOrderSummary)}
                            </div>
                        )}
                    </div>
                </div>

                <div className="min-h-0 min-w-0 overflow-y-auto pr-1 xl:h-full">
                    {openedOrders.length === 0 ? (
                        <div className="flex min-h-[420px] items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-dark-700 bg-white/70 dark:bg-dark-900/70">
                            <div className="text-center">
                                <DocumentTextIcon className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-600" />
                                <p className="mt-3 text-sm font-medium text-gray-800 dark:text-gray-200">Open an order email</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Click one order from the list.</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid h-full grid-cols-1 gap-4">
                            {openedOrders.map(renderOpenedOrder)}
                        </div>
                    )}
                </div>
            </div>

            <AutomationDialog
                open={automationDialog.open}
                order={automationDialog.order}
                onClose={closeAutomationDialog}
                apiUrl={API_URL}
                requestHeaders={requestHeaders}
                onOrderRefresh={refreshOrder}
            />

            <BatchAutomationDialog
                open={batchAutomationOpen}
                onClose={() => setBatchAutomationOpen(false)}
                apiUrl={API_URL}
                requestHeaders={requestHeaders}
                onOrdersRefresh={() => {
                    fetchOrders();
                    fetchFolderCounts();
                }}
            />

            <Dialog
                open={Boolean(dialogMode)}
                handler={closeDialog}
                size="lg"
                className="max-h-[calc(100vh-3rem)] overflow-y-auto dark:bg-dark-900"
            >
                <DialogHeader className="dark:text-gray-100">
                    {dialogMode === 'edit' ? 'Edit Order' : 'Add Order'}
                </DialogHeader>
                <DialogBody>
                    <div className="space-y-4">
                        <Input
                            label="Email"
                            value={formData.email}
                            onChange={(event) => setFormData(current => ({ ...current, email: event.target.value }))}
                            className="dark:text-gray-100"
                            labelProps={{ className: "dark:text-gray-400" }}
                        />

                        <Select
                            label="Folder"
                            value={formData.folder}
                            onChange={(value) => setFormData(current => ({ ...current, folder: value }))}
                            className="dark:text-gray-100"
                            labelProps={{ className: "dark:text-gray-400" }}
                        >
                            {FOLDERS.map(folder => (
                                <Option key={folder.value} value={folder.value}>{folder.label}</Option>
                            ))}
                        </Select>

                        <Input
                            label="Dolphin / Espanso profile number"
                            type="number"
                            min="1"
                            max="99"
                            value={formData.automationProfileNumber}
                            onChange={(event) => setFormData(current => ({
                                ...current,
                                automationProfileNumber: event.target.value,
                            }))}
                            error={Boolean(
                                formData.automationProfileNumber
                                && (!/^\d{1,2}$/.test(formData.automationProfileNumber)
                                    || Number(formData.automationProfileNumber) < 1
                                    || Number(formData.automationProfileNumber) > 99)
                            )}
                            className="dark:text-gray-100"
                            labelProps={{ className: "dark:text-gray-400" }}
                        />

                        <Input
                            label="Note"
                            value={formData.note}
                            onChange={(event) => setFormData(current => ({ ...current, note: event.target.value }))}
                            className="dark:text-gray-100"
                            labelProps={{ className: "dark:text-gray-400" }}
                        />

                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Content</label>
                            <textarea
                                value={formData.rawContent}
                                onChange={(event) => setFormData(current => ({ ...current, rawContent: event.target.value }))}
                                className="min-h-[320px] w-full rounded-lg border border-gray-300 dark:border-dark-700 bg-white dark:bg-dark-800 p-3 font-mono text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-700"
                                spellCheck={false}
                            />
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter className="flex items-center justify-end gap-2">
                    <Button variant="text" color="black" onClick={closeDialog} className="mv-secondary-button">
                        <XMarkIcon className="h-4 w-4" />
                        Cancel
                    </Button>
                    <Button
                        className="mv-primary-button"
                        onClick={saveOrder}
                        disabled={
                            isSaving
                            || !formData.email.trim()
                            || Boolean(
                                formData.automationProfileNumber
                                && (!/^\d{1,2}$/.test(formData.automationProfileNumber)
                                    || Number(formData.automationProfileNumber) < 1
                                    || Number(formData.automationProfileNumber) > 99)
                            )
                        }
                    >
                        <CheckCircleIcon className="h-4 w-4" />
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogFooter>
            </Dialog>

            <Dialog
                open={refundCandidateDialog.open}
                handler={closeRefundCandidateDialog}
                size="md"
                className="dark:bg-dark-900"
            >
                <DialogHeader className="block dark:text-gray-100">
                    <Typography variant="h5" className="text-gray-900 dark:text-gray-100">
                        Select refund item
                    </Typography>
                    <p className="mt-1 text-sm font-normal text-gray-500 dark:text-gray-400">
                        Choose an existing purchased item record. No new purchased item will be created.
                    </p>
                </DialogHeader>
                <DialogBody>
                    <div className="space-y-3">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-700 dark:bg-dark-850">
                            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Order item
                            </p>
                            <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {refundCandidateDialog.item?.title || '/'}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Paid price: {formatCurrency(refundCandidateDialog.item?.priceGbp)}
                                {' · '}
                                Purchased: {formatOrderPurchaseDate(refundCandidateDialog.order?.purchaseDateText)}
                            </p>
                        </div>

                        <div>
                            <Input
                                label="Search purchased item"
                                value={refundCandidateDialog.searchTerm}
                                onChange={(event) => setRefundCandidateDialog(current => ({
                                    ...current,
                                    searchTerm: event.target.value,
                                    selectedItemId: null,
                                }))}
                                disabled={savingRefundItems[getItemActionKey(refundCandidateDialog.order || {}, refundCandidateDialog.item || {})]}
                                className="dark:text-gray-100"
                                labelProps={{ className: "dark:text-gray-400" }}
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Edit the title if the purchased record uses a slightly different name.
                            </p>
                        </div>

                        {refundCandidateDialog.isLoading ? (
                            <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                Searching purchased item...
                            </div>
                        ) : refundCandidateDialog.candidates.length === 0 ? (
                            <div className="rounded-lg border border-rose-100 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-400/20 dark:bg-rose-400/10 dark:text-rose-300">
                                No purchased item candidates found. Refund was not created.
                            </div>
                        ) : (
                            <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
                                {refundCandidateDialog.candidates.map(candidate => {
                                    const isSelected = refundCandidateDialog.selectedItemId === candidate.id;

                                    return (
                                        <button
                                            key={candidate.id}
                                            type="button"
                                            onClick={() => setRefundCandidateDialog(current => ({
                                                ...current,
                                                selectedItemId: candidate.id,
                                            }))}
                                            className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                                                isSelected
                                                    ? 'border-cyan-700 bg-cyan-50 dark:border-panda-cyan/50 dark:bg-panda-cyan/10'
                                                    : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-dark-700 dark:bg-dark-900 dark:hover:bg-dark-800'
                                            }`}
                                        >
                                            <Avatar
                                                className="h-10 w-10 rounded-full"
                                                src={candidate.imageUrl || '/images/arcado-logo-solo.png'}
                                                alt={candidate.title || 'Item'}
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                    {candidate.title}
                                                </p>
                                                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                                                    <span>{candidate.author ? `By ${candidate.author}` : 'No author'}</span>
                                                    <span className="font-semibold text-purple-800 dark:text-purple-400">
                                                        {formatCurrency(candidate.price)}
                                                    </span>
                                                    <span>{formatDisplayDate(candidate.purchasedDate)}</span>
                                                    {candidate.status && (
                                                        <span className={getStatusBadgeClass(candidate.status)}>
                                                            {STATUS_LABELS[candidate.status] || candidate.status}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <span className={`h-4 w-4 shrink-0 rounded-full border ${
                                                isSelected
                                                    ? 'border-cyan-700 bg-cyan-700 dark:border-panda-cyan dark:bg-panda-cyan'
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`} />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </DialogBody>
                <DialogFooter className="flex items-center justify-end gap-2">
                    <Button variant="text" color="black" onClick={closeRefundCandidateDialog} className="mv-secondary-button">
                        <XMarkIcon className="h-4 w-4" />
                        Cancel
                    </Button>
                    <Button
                        className="mv-primary-button"
                        onClick={addRefundFromSelectedItem}
                        disabled={
                            refundCandidateDialog.isLoading ||
                            !refundCandidateDialog.selectedItemId ||
                            savingRefundItems[getItemActionKey(refundCandidateDialog.order || {}, refundCandidateDialog.item || {})]
                        }
                    >
                        <CheckCircleIcon className="h-4 w-4" />
                        {savingRefundItems[getItemActionKey(refundCandidateDialog.order || {}, refundCandidateDialog.item || {})]
                            ? 'Adding...'
                            : 'Add refund'}
                    </Button>
                </DialogFooter>
            </Dialog>

            <Dialog
                open={repeatActionDialog.open}
                handler={closeRepeatActionDialog}
                size="xs"
                className="dark:bg-dark-900"
            >
                <DialogHeader className="dark:text-gray-100">
                    Action already added
                </DialogHeader>
                <DialogBody>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                        This {repeatActionDialog.type === 'replacement' ? 'replacement' : 'refund'} was already added
                        {repeatActionDialog.previousDate ? ` on ${repeatActionDialog.previousDate}` : ''}.
                    </p>
                    <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                        Are you sure you want to add it again?
                    </p>
                </DialogBody>
                <DialogFooter className="flex items-center justify-end gap-2">
                    <Button variant="text" color="black" onClick={closeRepeatActionDialog} className="mv-secondary-button">
                        <XMarkIcon className="h-4 w-4" />
                        Cancel
                    </Button>
                    <Button className="mv-primary-button" onClick={confirmRepeatAction}>
                        <CheckCircleIcon className="h-4 w-4" />
                        Yes, continue
                    </Button>
                </DialogFooter>
            </Dialog>

            <Dialog
                open={eventDialog.open}
                handler={closeEventDialog}
                size="sm"
                className="flex max-h-[calc(100vh-2rem)] flex-col overflow-hidden dark:bg-dark-900"
            >
                <DialogHeader className="shrink-0 dark:text-gray-100">
                    {eventForm.id ? 'Edit' : 'Add'} {getEventTypeLabel(eventForm.type)} event
                </DialogHeader>
                <DialogBody className="min-h-0 flex-1 overflow-y-auto">
                    <div className="space-y-4">
                        {!TICKET_EVENT_TYPES.includes(eventForm.type) && !REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES.includes(eventForm.type) && (
                            <Input
                                label="Date and time"
                                type="datetime-local"
                                value={eventForm.eventDateInputValue}
                                onChange={(event) => {
                                    const eventDateInputValue = event.target.value;
                                    setEventForm(current => ({
                                        ...current,
                                        eventDateInputValue,
                                        eventDateText: formatEventDateText(eventDateInputValue),
                                    }));
                                }}
                                className="dark:text-gray-100"
                                labelProps={{ className: "dark:text-gray-400" }}
                            />
                        )}

                        {TICKET_EVENT_TYPES.includes(eventForm.type) && (
                            <>
                            {(eventForm.ticketEntries.length > 0
                                ? eventForm.ticketEntries
                                : [createTicketEntry(eventForm.eventDateInputValue || toDateTimeInputValue(), eventForm.ticketNumber)]
                            ).map((entry, index) => {
                                const groupNumber = index + 1;
                                const isFirstGroup = index === 0;

                                return (
                                    <div key={entry.id} className="rounded-md border border-gray-100 p-3 dark:border-dark-700">
                                        <div className="mb-3 flex items-center justify-between">
                                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                                                Ticket group {groupNumber}
                                            </span>
                                            {!isFirstGroup && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeTicketEntry(entry.id)}
                                                    title={`Remove ticket group ${groupNumber}`}
                                                    className="flex h-8 w-8 items-center justify-center rounded-md border border-red-100 text-red-700 hover:bg-red-50 dark:border-panda-pink/30 dark:text-panda-pink dark:hover:bg-panda-pink/10"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                        <div className="space-y-4">
                                            <Input
                                                label="Date and time"
                                                type="datetime-local"
                                                value={isFirstGroup ? eventForm.eventDateInputValue : entry.eventDateInputValue}
                                                onChange={(event) => {
                                                    const eventDateInputValue = event.target.value;
                                                    const eventDateText = formatEventDateText(eventDateInputValue);
                                                    if (isFirstGroup) {
                                                        setEventForm(current => ({
                                                            ...current,
                                                            eventDateInputValue,
                                                            eventDateText,
                                                            ticketEntries: (current.ticketEntries.length > 0
                                                                ? current.ticketEntries
                                                                : [createTicketEntry(eventDateInputValue, current.ticketNumber)]
                                                            ).map((ticketEntry, entryIndex) => entryIndex === 0 ? {
                                                                ...ticketEntry,
                                                                eventDateInputValue,
                                                                eventDateText,
                                                            } : ticketEntry),
                                                        }));
                                                        return;
                                                    }

                                                    syncTicketEntry(index, { eventDateInputValue, eventDateText });
                                                }}
                                                className="dark:text-gray-100"
                                                labelProps={{ className: "dark:text-gray-400" }}
                                            />
                                            <Input
                                                label="Ticket number"
                                                value={isFirstGroup ? eventForm.ticketNumber : entry.ticketNumber}
                                                onChange={(event) => {
                                                    const ticketNumber = event.target.value;
                                                    if (isFirstGroup) {
                                                        setEventForm(current => ({
                                                            ...current,
                                                            ticketNumber,
                                                            ticketEntries: (current.ticketEntries.length > 0
                                                                ? current.ticketEntries
                                                                : [createTicketEntry(current.eventDateInputValue || toDateTimeInputValue(), ticketNumber)]
                                                            ).map((ticketEntry, entryIndex) => entryIndex === 0 ? { ...ticketEntry, ticketNumber } : ticketEntry),
                                                        }));
                                                        return;
                                                    }

                                                    syncTicketEntry(index, { ticketNumber });
                                                }}
                                                className="dark:text-gray-100"
                                                labelProps={{ className: "dark:text-gray-400" }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}

                            {eventForm.type !== 'REPLACEMENT_TITLE_SUBMITTED' && (
                                <button
                                    type="button"
                                    onClick={addTicketEntry}
                                    title="Add ticket group"
                                    className="flex h-9 w-9 items-center justify-center rounded-md border border-cyan-200 text-cyan-800 hover:bg-cyan-50 dark:border-panda-cyan/30 dark:text-panda-cyan dark:hover:bg-panda-cyan/10"
                                >
                                    <PlusCircleIcon className="h-5 w-5" />
                                </button>
                            )}
                            </>
                        )}

                        {REPLACEMENT_TITLE_SUBMITTED_EVENT_TYPES.includes(eventForm.type) && (
                            <>
                            {(eventForm.replacementTitleEntries.length > 0
                                ? eventForm.replacementTitleEntries
                                : [createReplacementTitleEntry(
                                    eventForm.eventDateInputValue || toDateTimeInputValue(),
                                    eventForm.replacementTitle,
                                    eventForm.eanIsbn
                                )]
                            ).map((entry, index) => {
                                const groupNumber = index + 1;
                                const isFirstGroup = index === 0;

                                return (
                                    <div key={entry.id} className="rounded-md border border-gray-100 p-3 dark:border-dark-700">
                                        <div className="mb-3 flex items-center justify-between">
                                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                                                Input group {groupNumber}
                                            </span>
                                            {!isFirstGroup && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeReplacementTitleEntry(entry.id)}
                                                    title={`Remove input group ${groupNumber}`}
                                                    className="flex h-8 w-8 items-center justify-center rounded-md border border-red-100 text-red-700 hover:bg-red-50 dark:border-panda-pink/30 dark:text-panda-pink dark:hover:bg-panda-pink/10"
                                                >
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                        <div className="space-y-4">
                                            <Input
                                                label="Date and time"
                                                type="datetime-local"
                                                value={entry.eventDateInputValue}
                                                onChange={(event) => {
                                                    const eventDateInputValue = event.target.value;
                                                    syncReplacementTitleEntry(index, {
                                                        eventDateInputValue,
                                                        eventDateText: formatEventDateText(eventDateInputValue),
                                                    });
                                                }}
                                                className="dark:text-gray-100"
                                                labelProps={{ className: "dark:text-gray-400" }}
                                            />
                                            <Input
                                                label={getEventTypeLabel(eventForm.type)}
                                                value={entry.replacementTitle}
                                                onChange={(event) => syncReplacementTitleEntry(index, { replacementTitle: event.target.value })}
                                                className="dark:text-gray-100"
                                                labelProps={{ className: "dark:text-gray-400" }}
                                            />
                                            <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 dark:border-dark-700 dark:bg-dark-850">
                                                <Checkbox
                                                    label={
                                                        <Typography className="ml-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Same item
                                                        </Typography>
                                                    }
                                                    color="cyan"
                                                    ripple={false}
                                                    checked={Boolean(entry.sameItem)}
                                                    onChange={(event) => syncReplacementTitleEntry(index, {
                                                        sameItem: event.target.checked,
                                                        ...(event.target.checked ? { eanIsbn: '' } : {}),
                                                    })}
                                                    className="h-5 w-5 border-gray-300 bg-white hover:before:opacity-0 checked:border-cyan-700 checked:bg-cyan-700 dark:border-gray-600 dark:bg-dark-900 dark:checked:border-cyan-600 dark:checked:bg-cyan-600"
                                                    containerProps={{ className: "p-1" }}
                                                    labelProps={{ className: "flex items-center" }}
                                                    crossOrigin={undefined}
                                                />
                                            </div>
                                            <Input
                                                label="EAN/ISBN"
                                                value={entry.eanIsbn}
                                                onChange={(event) => syncReplacementTitleEntry(index, { eanIsbn: event.target.value })}
                                                disabled={Boolean(entry.sameItem)}
                                                className="disabled:bg-gray-100 disabled:text-gray-500 dark:text-gray-100 dark:disabled:bg-dark-800 dark:disabled:text-gray-500"
                                                labelProps={{ className: "dark:text-gray-400" }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}

                            <button
                                type="button"
                                onClick={addReplacementTitleEntry}
                                title="Add input group"
                                className="flex h-9 w-9 items-center justify-center rounded-md border border-cyan-200 text-cyan-800 hover:bg-cyan-50 dark:border-panda-cyan/30 dark:text-panda-cyan dark:hover:bg-panda-cyan/10"
                            >
                                <PlusCircleIcon className="h-5 w-5" />
                            </button>
                            </>
                        )}

                        {APPROVED_REPLACEMENT_EVENT_TYPES.includes(eventForm.type) && (
                            <Input
                                label="Replacement Title"
                                value={eventForm.replacementTitle}
                                onChange={(event) => setEventForm(current => ({ ...current, replacementTitle: event.target.value }))}
                                className="dark:text-gray-100"
                                labelProps={{ className: "dark:text-gray-400" }}
                            />
                        )}

                        {APPROVED_REPLACEMENT_EVENT_TYPES.includes(eventForm.type) && (
                            <Input
                                label="Agent"
                                value={eventForm.agentName}
                                onChange={(event) => setEventForm(current => ({ ...current, agentName: event.target.value }))}
                                className="dark:text-gray-100"
                                labelProps={{ className: "dark:text-gray-400" }}
                            />
                        )}

                        {APPROVED_REPLACEMENT_EVENT_TYPES.includes(eventForm.type) && (
                            <Input
                                label="Note"
                                value={eventForm.note}
                                onChange={(event) => setEventForm(current => ({ ...current, note: event.target.value }))}
                                className="dark:text-gray-100"
                                labelProps={{ className: "dark:text-gray-400" }}
                            />
                        )}
                    </div>
                </DialogBody>
                <DialogFooter className="flex shrink-0 items-center justify-end gap-2">
                    {eventForm.id && (
                        <button
                            type="button"
                            onClick={() => deleteItemEvent(eventForm.id)}
                            className={`mr-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-md border text-xs font-semibold transition-colors ${
                                eventDeleteTimers[eventForm.id]
                                    ? 'border-red-300 bg-red-50 text-red-800 dark:border-panda-pink/40 dark:bg-panda-pink/10 dark:text-panda-pink'
                                    : 'border-red-100 text-red-700 hover:bg-red-50 dark:border-panda-pink/30 dark:text-panda-pink dark:hover:bg-panda-pink/10'
                            }`}
                        >
                            {eventDeleteTimers[eventForm.id] ? eventDeleteTimers[eventForm.id] : <TrashIcon className="h-4 w-4" />}
                        </button>
                    )}
                    <Button variant="text" color="black" onClick={closeEventDialog} className="mv-secondary-button">
                        <XMarkIcon className="h-4 w-4" />
                        Cancel
                    </Button>
                    <Button
                        className="mv-primary-button"
                        onClick={saveItemEvent}
                        disabled={isEventSaveDisabled()}
                    >
                        <CheckCircleIcon className="h-4 w-4" />
                        Save
                    </Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
}
